/** sessionStorage cache shared between SessionManager and main-content (same key). */

import { isDemoSession } from "./demoVisitStore";

export function getSessionsCacheKey(userId) {
  if (userId == null || userId === "") return null;
  return `session-manager-cache:${String(userId)}`;
}

/** Drop soft-deleted sessions from sidebar list payloads. */
export function isSessionDeleted(session) {
  return session?.status === "deleted";
}

export function filterVisibleSessions(sessions) {
  if (!Array.isArray(sessions)) return [];
  return sessions.filter(
    (session) => !isSessionDeleted(session) && !isDemoSession(session),
  );
}

/** Sort key: creation time first, then visit time (not locale date strings). */
export function getSessionSortTime(session) {
  const raw = session?.createdAt ?? session?.visitDate ?? null;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

export function compareSessionsNewestFirst(a, b) {
  const diff = getSessionSortTime(b) - getSessionSortTime(a);
  if (diff !== 0) return diff;
  return String(b?.id ?? b?.sessionId ?? "").localeCompare(
    String(a?.id ?? a?.sessionId ?? ""),
  );
}

export function sortSessionsNewestFirst(sessions) {
  if (!Array.isArray(sessions)) return [];
  return [...sessions].sort(compareSessionsNewestFirst);
}

export function readSessionsListCache(userId) {
  const key = getSessionsCacheKey(userId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getStoredActiveSessionId() {
  if (typeof window === "undefined") return null;
  const sid = localStorage.getItem("activeSessionId");
  if (!sid || sid === "undefined" || sid === "null") return null;
  return sid;
}

export function findCachedSession(cached, sessionId) {
  if (!cached?.sessions?.length || !sessionId) return null;
  const target = String(sessionId);
  return (
    cached.sessions.find(
      (s) => String(s?.id ?? s?.sessionId) === target,
    ) ?? null
  );
}

export function removeSessionFromListCache(userId, sessionId) {
  const key = getSessionsCacheKey(userId);
  if (!key || !sessionId || typeof window === "undefined") return;
  try {
    const cached = readSessionsListCache(userId);
    if (!cached?.sessions?.length) return;
    const target = String(sessionId);
    const sessions = filterVisibleSessions(cached.sessions).filter(
      (s) => String(s?.id ?? s?.sessionId) !== target,
    );
    const loadedSessionIds = (cached.loadedSessionIds || [])
      .map(String)
      .filter((id) => id !== target);
    sessionStorage.setItem(
      key,
      JSON.stringify({
        ...cached,
        sessions,
        loadedSessionIds,
      }),
    );
  } catch {
    // ignore cache write failures
  }
}

export function canFastRestoreFromCache(userId, sessionId) {
  if (!sessionId) return false;
  const cached = readSessionsListCache(userId);
  return !!findCachedSession(cached, sessionId);
}
